import { Injectable, signal, computed, effect } from '@angular/core';

type Theme = 'light' | 'dark' | 'system';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'lexium-theme';

  private readonly _preference = signal<Theme>(this.loadPreference());

  readonly isDark = computed(() => {
    const pref = this._preference();
    if (pref === 'dark') return true;
    if (pref === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  readonly preference = this._preference.asReadonly();

  constructor() {
    this.apply();

    // React to signal changes
    effect(() => {
      this.apply();
    });

    // React to OS preference changes when set to 'system'
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (this._preference() === 'system') {
        this.apply();
      }
    });
  }

  toggle(): void {
    this._preference.set(this.isDark() ? 'light' : 'dark');
    this.save();
  }

  setTheme(theme: Theme): void {
    this._preference.set(theme);
    this.save();
  }

  private apply(): void {
    const dark = this.isDark();
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }

  private save(): void {
    localStorage.setItem(this.STORAGE_KEY, this._preference());
  }

  private loadPreference(): Theme {
    const saved = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
    return 'system';
  }
}
